import { Component, computed, input, output, OnInit, ViewChild } from '@angular/core';
import SeoWorkTaskType = Boo.Objects.Work.SeoMinute.SeoWorkTaskType;
import WorkRequestDetail = Boo.Objects.Work.SeoMinute.WorkRequestDetail;
import FlagTypes = Boo.Objects.Work.SeoMinute.Enums.FlagTypes;
import { TaskTypeWorkGroup, WorkAdded, WorkQtyUpdated, WorkPeriod } from '../Models/models';
import { DisplayStatuses } from '../Models/display-statuses';
import { TaskTypePeriodSelectionComponent } from '../Components/task-type-period-selection/task-type-period-selection.component';
import { Periods } from './../Models/periods';
import { DEFAULT_MAX_TASK_COUNT } from '../../../../shared/constants';

@Component({
  selector: 'app-components-managecustomer-work-planner',
  templateUrl: './work-planner.component.html'
})
export class WorkPlannerComponent implements OnInit{
  currentPeriod = input<WorkPeriod>();
  futurePeriods = input<WorkPeriod[]>([])
  taskTypes = input<SeoWorkTaskType[]>([]);
  customer = input<Boo.Objects.Customer>();
  user = input<Boo.Objects.User>();
  periodDetails = input<Boo.Objects.Work.SeoMinute.PeriodDetails[]>([]);

  workAdded = output<WorkAdded>();
  workQtyUpdated = output<WorkQtyUpdated>();
  workRemoved = output<WorkRequestDetail>();
  editWorkRequest = output<WorkRequestDetail>();
  refresh = output();

  allPeriods = computed<WorkPeriod[]>(() => [this.currentPeriod(), ...this.futurePeriods()]);
  allocatedMinutesByPeriod = computed<Map<number, number>>(() => new Map<number, number>(this.allPeriods().map(
    period => [period.period, period.taskTypeGroups.reduce((acc, group) => acc + group.workItems.length * group.taskTypeMinutes, 0)])));

  qtyNumbers: number[] = [];
  openedTaskGroups = new Set<TaskTypeWorkGroup>();
  noAvailablePeriodsErrorMessage: string;
  @ViewChild('taskTypePeriodSelection') taskTypePeriodSelectionComponent!: TaskTypePeriodSelectionComponent;

  ngOnInit() {
    for (let number = 1; number <= DEFAULT_MAX_TASK_COUNT; number++) {
      this.qtyNumbers.push(number);
    }
  }

  groupQtyChange(group: TaskTypeWorkGroup, quantity: number, period: number): void {
    this.workQtyUpdated.emit({ type: this.taskTypes().find(x => x.TaskTypeId === group.taskTypeId), quantity, period: period });
  }

  canDeleteGroup(group: TaskTypeWorkGroup): boolean {
    return group.workItems.every(x => x.CanDelete);
  }

  amountOfItemsCantBeDeleted(group: TaskTypeWorkGroup): number {
    return group.workItems.filter(x => !x.CanDelete).length;
  }

  allocatedMinutesForPeriod(period: WorkPeriod): number {
    return this.allocatedMinutesByPeriod().get(period.period)
  }

  qtyDisabled(qty: number, group: TaskTypeWorkGroup): boolean {
    return qty < this.amountOfItemsCantBeDeleted(group) || qty > group.maxCount || ((group.isNonworkable || group.isTemporarilyNonworkable) && qty > group.workItems.length);
  }

  addTaskType(type: SeoWorkTaskType, period: number, quantity: number): void {
    const periodTaskCount = this.allPeriods().find(x => x.period === period)?.taskTypeGroups.find(x => x.taskTypeId === type.TaskTypeId)?.workItems.length ?? 0;
    this.workQtyUpdated.emit({type: type, period: period, quantity: quantity + periodTaskCount});
    this.taskTypePeriodSelectionComponent.clearSelectedTaskType();
  }

  addCustomTaskType(workAdded: WorkAdded): void {
    this.workAdded.emit(workAdded);
    this.taskTypePeriodSelectionComponent.clearSelectedTaskType();
  }

  deleteCustomItem(item: WorkRequestDetail): void {
    this.workRemoved.emit(item);
  }

  toggleOpenCustomTaskGroup(group: TaskTypeWorkGroup): void {
    if (this.openedTaskGroups.has(group)) {
      this.openedTaskGroups.delete(group);
    } else {
      this.openedTaskGroups.add(group);
    }
  }

  getGroupStatus(group: WorkRequestDetail[]): string {
    if (group.some(x => x.WorkStatusDisplayName === DisplayStatuses.processing)) {
      return DisplayStatuses.processing;
    } else if (group.some(x => x.WorkStatusDisplayName === DisplayStatuses.pending)) {
      return DisplayStatuses.pending;
    } else if (group.some(x => x.WorkStatusDisplayName !== DisplayStatuses.complete)) {
      return DisplayStatuses.incomplete;
    } else {
      return DisplayStatuses.complete;
    }
  }

  addNextAvailableItem(type: SeoWorkTaskType): void {
    if (type.IsCustom && type.TaskTypeEnum === Boo.Objects.Enums.TaskTypeEnum.Custom) {
      this.noAvailablePeriodsErrorMessage = `Cannot add ${type.Name} using Next Available. Please select a specific period and try again.`;
      return;
    }

    const workPeriods = [this.currentPeriod(), ...this.futurePeriods()].sort((a, b) => a.period - b.period);
	  const availableWorkPeriod = this.findAvailablePeriodForType(workPeriods, type, true);

    if (!availableWorkPeriod) {
      const hasMinutesWithoutBusinessCitations = !!this.findAvailablePeriodForType(workPeriods, type, false);
      this.noAvailablePeriodsErrorMessage = hasMinutesWithoutBusinessCitations
        ? `Cannot add ${type.Name}. Required Local Business Citation Subscriptions would exceed available minutes in all periods.`
        : `Cannot add ${type.Name} because there are no available periods with enough minutes.`;
      return;
    }

    if (type.IsCustom) {
      this.addCustomTaskType({ type, period: availableWorkPeriod.period });
    } else {
      this.addTaskType(type, availableWorkPeriod.period, 1);
    }
  }

  clearNoAvailablePeriodsErrorMessage(): void {
    this.noAvailablePeriodsErrorMessage = undefined;
  }

  private findAvailablePeriodForType(workPeriods: WorkPeriod[], type: SeoWorkTaskType, includeLbcsTasks: boolean): WorkPeriod {
    return workPeriods.find(workPeriod => {
      if (workPeriod.period === Periods.current && type.Flags?.some(x => x.Type === FlagTypes.ExaustedForPeriod)) {
        return false;
      }

      return this.hasMinutesForPeriod(workPeriod, type, includeLbcsTasks) && this.hasNotExceededMaxForPeriod(workPeriod, type);
    });
  }

  private hasMinutesForPeriod(period: WorkPeriod, type: SeoWorkTaskType, includeLbcsTasks: boolean): boolean {
    let lbcsMinutes = 0;
    if (includeLbcsTasks) {
      const lbcsTaskGroup = period.taskTypeGroups
        .find(x => x.taskTypeId === Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription)

      if (lbcsTaskGroup) {
        lbcsMinutes = lbcsTaskGroup.workItems.length * lbcsTaskGroup.taskTypeMinutes
      } else {
        const lbcsTaskTypeMinutes = this.taskTypes().find(x => x.TaskTypeEnum === Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription)?.Minutes ?? 0;
        const lbcsTaskCount = period.LocalBusinessCitationSubscriptionTaskCount ?? 0;
        lbcsMinutes = lbcsTaskCount * lbcsTaskTypeMinutes;
      }
    }

    let nonLbcsMinutes = period.taskTypeGroups
      .filter(x => x.taskTypeId !== Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription)
      .reduce((acc, group) => acc + group.workItems.length * group.taskTypeMinutes, 0);

    // Reserve minutes for one PremiumMonthlyCampaignUpdate task if:
    //   - work has not yet been generated for this period
    //   - work being added is not a PremiumMonthlyCampaignUpdate task
    //   - a PremiumMonthlyCampaignUpdate task has not been allocated for this period
    // (Note: In these conditions we will always reserve these minutes, even if there
    //    are not enough minutes available. This may cause Next Available to skip a
    //    period that would otherwise have enough minutes. This is intended.)
    const hasWorkGeneratedForPeriod = period.taskTypeGroups.some(x => x.workItems.some(y => y.WorkRequest.WorkOrderItemId));
    if (!hasWorkGeneratedForPeriod && type.TaskTypeEnum !== Boo.Objects.Enums.TaskTypeEnum.PremiumMonthlyCampaignUpdate && !period.taskTypeGroups.find(x => x.taskTypeId === Boo.Objects.Enums.TaskTypeEnum.PremiumMonthlyCampaignUpdate)) {
      const periodDetails = this.periodDetails().find(x => x.Period === period.period);
      const pmcuMinutesToReserve = periodDetails?.PartnerTaskTypeAllocations.find(x => x.TaskTypeId === Boo.Objects.Enums.TaskTypeEnum.PremiumMonthlyCampaignUpdate)?.MinutesPerTask ?? 0;
      nonLbcsMinutes += pmcuMinutesToReserve;
    }

    const totalAllocatedMinutes = lbcsMinutes + nonLbcsMinutes;

    return totalAllocatedMinutes + type.Minutes <= period.minutes;
  }

  private hasNotExceededMaxForPeriod(period: WorkPeriod, type: SeoWorkTaskType) : boolean {
    return (period.taskTypeGroups.find(x => x.taskTypeId === type.TaskTypeId)?.workItems?.length ?? 0) < (type.MaxCount ?? DEFAULT_MAX_TASK_COUNT);
  }
}